import { useMemo } from "react";
import { useCurrentUser, useDashboardLoans, useDashboardAllLoans, useLoanSummaryMetrics } from "../services/api";
import { useAuth } from "../services/authService";
import { TokenManager } from "../utils/tokenManager";
import { Loan, LoanStats, LoanSummaryMetrics } from "../types/loan.types";

// ─────────────────────────────────────────────────────────────────────────────
// Transform one raw DashboardLoan into the app-level Loan
// ─────────────────────────────────────────────────────────────────────────────
const transformLoan = (raw: any): Loan => {
  const nameParts = (raw.client?.name || "").trim().split(/\s+/);
  return {
    id: raw.loanId,
    clientFirstName: nameParts[0] ?? "N/A",
    clientLastName: nameParts.slice(1).join(" ") || "N/A",
    clientType: raw.client?.type || "N/A",
    gmv: Number(raw.offerDetails?.approvedAmount) || 0,
    status: raw.status,
    system: raw.system,
    country: raw.country || "N/A",
    updatedAt: new Date(raw.updatedAt ?? raw.lastUpdated),
    createdAt: new Date(raw.createdAt || Date.now()),
    vehicleInformation: raw.vehicleInformation,
    offerDetails: raw.offerDetails,
    additionalFees: raw.additionalFees,
    carId: raw.carId || raw.car_id,
    message: raw.message,
    documents: raw.documents,
    task_statuses: raw.task_statuses ? [...raw.task_statuses].sort((a, b) => (a.task || 0) - (b.task || 0)) : undefined,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Public hook
// ─────────────────────────────────────────────────────────────────────────────
export const useLoanData = (
  selectedCountry: string = "ALL",
  page: number = 1,
  pageSize: number = 10
) => {
  const { data: authState } = useAuth();
  const isAdmin = authState?.authority?.roles?.includes("DASHBOARD_ADMIN");

  // Determine if we are in "All Countries" mode for admins
  const isGlobalView = isAdmin && selectedCountry === "ALL";

  // Resolve user's profile country for fallback
  const storedUser = TokenManager.getUserData();
  const { data: fetchedUser, isLoading: userLoading } = useCurrentUser();
  const profileCountry = storedUser?.country || fetchedUser?.country || "";

  // Effective country to use for country-specific API calls
  const apiCountry = isGlobalView ? "" : (selectedCountry === "ALL" ? profileCountry : selectedCountry);

  // 1. Fetch from Global API (used by admins for "ALL")
  const {
    data: globalData,
    isLoading: globalLoading,
    error: globalError,
    refetch: refetchGlobal,
  } = useDashboardAllLoans(page, pageSize, isGlobalView);

  // 2. Fetch from Country API (used for specific country or non-admin view)
  const {
    data: countryData,
    isLoading: countryLoading,
    error: countryError,
    refetch: refetchCountry,
  } = useDashboardLoans(apiCountry, page, pageSize);

  // 3. Fetch summary metrics from the new API
  const {
    data: summaryMetrics,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
  } = useLoanSummaryMetrics(apiCountry);

  // Combine data based on which hook is active
  const resultData = useMemo(() => {
    const activeData = isGlobalView ? globalData : countryData;

    if (!activeData?.loans) {
      return {
        loans: [],
        pagination: { total: 0, currentPage: page, pageSize },
      };
    }

    return {
      loans: activeData.loans.map(transformLoan),
      pagination: activeData.pagination,
    };
  }, [isGlobalView, globalData, countryData, page, pageSize]);

  // Map summary metrics to the UI's LoanStats format
  const stats: LoanStats = useMemo(() => {
    if (!summaryMetrics) {
      return {
        todayCreated: 0,
        todayPendingCustomerDocs: 0,
        todayPendingSignedDocs: 0,
        todayPendingFulfilment: 0,
        equityUploadedThisWeek: 0,
      };
    }

    return {
      todayCreated: summaryMetrics.completedLoansToday,
      todayPendingCustomerDocs: summaryMetrics.pendingKycDocumentsAllTime,
      todayPendingSignedDocs: summaryMetrics.pendingSignedLoanDocumentsAllTime,
      todayPendingFulfilment: summaryMetrics.fullEquityUploadsThisWeek,
      equityUploadedThisWeek: summaryMetrics.partialEquityUploadsThisWeek,
    };
  }, [summaryMetrics]);

  const isLoading = userLoading || metricsLoading || (isGlobalView ? globalLoading : countryLoading);
  const error = isGlobalView ? globalError : countryError;
  
  const refetch = async () => {
    await Promise.all([
      isGlobalView ? refetchGlobal() : refetchCountry(),
      refetchMetrics()
    ]);
  };

  return useMemo(() => ({
    loans: resultData.loans,
    stats,
    summaryMetrics,
    pagination: resultData.pagination,
    countryCode: isGlobalView ? "ALL" : apiCountry,
    isLoading,
    error: error ?? null,
    refetch,
    isAdmin,
  }), [resultData.loans, stats, summaryMetrics, resultData.pagination, isGlobalView, apiCountry, isLoading, error, isAdmin]);
};